/**
 * `LGH-043`, `ARC-006`. Umami page views for the operator console's own six
 * surfaces — `OPS-004`'s own real, live site.
 */
const UMAMI_SCRIPT_ORIGIN = 'https://analytics.pruvato.com'
const UMAMI_WEBSITE_ID = '0a92c0b2-0b7b-4a51-8f05-4673af6bbdbb'

interface UmamiPayload {
  url: string
  [key: string]: unknown
}

declare global {
  interface Window {
    umami?: { track: (resolve: (payload: UmamiPayload) => UmamiPayload) => void }
  }
}

let scriptRequested = false
let scriptLoaded = false
/**
 * The most recent pattern asked for before the script finished loading.
 * **A dynamically-created `<script>` runs as `async` regardless of the
 * `defer` attribute** (the attribute only affects scripts present in the
 * initial HTML parse) — found live, not assumed: the very first
 * `trackPageView` call of a session ran before `window.umami` existed,
 * every time, so it was silently dropped as a no-op. `defer` is kept on the
 * element anyway (harmless, correct intent), but this queue is what
 * actually fixes the race — only the LATEST pending pattern matters, since
 * an earlier one is a route the operator has already navigated away from by
 * the time the script is ready.
 */
let pendingPattern: string | null = null

function flushPending(): void {
  scriptLoaded = true
  if (pendingPattern !== null) {
    trackPageView(pendingPattern)
    pendingPattern = null
  }
}

/**
 * `data-auto-track="false"` — Umami's own default tracks every navigation
 * the SPA makes, which would include `/login`, the customer console, and
 * every other route this task's own scope explicitly excludes. Tracking is
 * opt-in per call (`trackPageView`) instead, fired only for the six named
 * operator surfaces (`PageViewTracker.tsx`).
 *
 * Fails quiet if the host never resolves or the request fails: no `onerror`
 * handler rethrows, `window.umami` simply never appears, and every
 * `trackPageView` call after that stays a safe no-op — never a thrown error
 * a visitor (or a test) would see.
 */
export function loadUmamiScript(): void {
  if (scriptRequested || typeof document === 'undefined') return
  scriptRequested = true

  const script = document.createElement('script')
  script.src = `${UMAMI_SCRIPT_ORIGIN}/script.js`
  script.defer = true
  script.dataset['websiteId'] = UMAMI_WEBSITE_ID
  script.dataset['autoTrack'] = 'false'
  script.addEventListener('load', flushPending)
  document.head.appendChild(script)
}

/**
 * A real page view, not a custom event — `umami.track(fn)`'s callback form
 * overrides the payload's own `url` before Umami sends it, which is what
 * keeps this a page-view record rather than a named event (`umami.track
 * (name)` is the event API, a different thing the task's own scope excludes
 * for v1). `routePattern` is a normalised pattern (`"/runs/:id"`), never the
 * literal URL, so a real run id never leaves the browser in an analytics
 * payload.
 *
 * Queues rather than drops when the script hasn't loaded yet — see
 * `pendingPattern`'s own comment for why that queue exists at all.
 */
export function trackPageView(routePattern: string): void {
  if (window.umami === undefined && !scriptLoaded) {
    pendingPattern = routePattern
    return
  }
  window.umami?.track((payload) => ({ ...payload, url: routePattern }))
}
